/**
 * Hook for loading and tracking canvas images from the Picsart editor
 */

import { useState, useCallback, useEffect } from 'react';

import { isContextAvailable } from '../miniappContext';
import { getCanvasImages, type CanvasImage } from '../utils/replayUtils';

export interface UseCanvasImagesResult {
  /** Array of images available on the canvas */
  images: CanvasImage[];
  /** Number of images available */
  imageCount: number;
  /** Whether images are currently being loaded */
  isLoading: boolean;
  /** Whether the canvas can be accessed (context available) */
  canAccessCanvas: boolean;
  /** Function to manually load/refresh images - returns the loaded images */
  loadImages: () => Promise<CanvasImage[]>;
}

/**
 * Hook to load and track canvas images from the Picsart editor
 * @param autoLoad - Whether to automatically load images when context becomes available (default: true)
 */
export function useCanvasImages(autoLoad = true): UseCanvasImagesResult {
  const [images, setImages] = useState<CanvasImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canAccessCanvas, setCanAccessCanvas] = useState(false);

  // Check if canvas is accessible
  useEffect(() => {
    const available = isContextAvailable();
    setCanAccessCanvas(available);
  }, []);

  // Load images from canvas - returns the images for immediate use
  const loadImages = useCallback(async (): Promise<CanvasImage[]> => {
    if (!isContextAvailable()) {
      setImages([]);
      setCanAccessCanvas(false);
      return [];
    }

    setIsLoading(true);
    setCanAccessCanvas(true);

    try {
      const canvasImages = await getCanvasImages();
      setImages(canvasImages);
      return canvasImages;
    } catch (error) {
      console.error('[useCanvasImages] Error loading canvas images:', error);
      setImages([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-load images on mount if context is available
  useEffect(() => {
    if (autoLoad && isContextAvailable()) {
      void loadImages();
    }
  }, [autoLoad, loadImages]);

  return {
    images,
    imageCount: images.length,
    isLoading,
    canAccessCanvas,
    loadImages,
  };
}

export default useCanvasImages;
